import { useParams, Link } from 'react-router-dom';
import WebsiteLayout from '@/components/layout/WebsiteLayout';
import { ArrowLeft, Shield, AlertTriangle, Leaf, Users, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const policies = {
  'anti-corruption': {
    title: 'Anti-Bribery and Anti-Corruption Policy',
    icon: Shield,
    content: `
      <h2>POLICY STATEMENT</h2>
      <p>Aurous Consultancy is committed to conducting business ethically and honestly, and has zero-tolerance for bribery and corrupt activities. We are committed in all business dealings and relationship and will constantly uphold all laws relating to anti-bribery and anti-corruption in Malaysia, in particular, the Malaysia Anti-Corruption Commission Act 2009.</p>
      
      <h2>SCOPE OF POLICY</h2>
      <p>The principles and obligations outlined in this policy apply to all employees of Aurous Consultancy, Board of Directors, and Aurous Consultancy's stakeholders, which include customers, vendors, agents, consultants, outsourced personnel and other representatives.</p>
      <p>This policy sets forth Aurous Consultancy minimum compliance standards concerning interactions with third parties. However, where local law and regulations require more stringent controls, then such stricter controls must be followed.</p>
      
      <h2>DEFINITION OF BRIBERY</h2>
      <p>A bribe refers to "offering, promising, giving or soliciting of undue advantage of any value (which could be financial or non-financial), directly or indirectly, and irrespective of location(s), in violation of applicable law, as an inducement or reward for a person acting or refraining from acting in relation to the performance of that person's duties" as defined by the Anti Bribery Management System ISO 37001.</p>
      
      <h2>FIGHTING BRIBERY, CORRUPTION AND UNETHICAL PRACTICES</h2>
      
      <h3>1. SOLICITATION, BRIBERY AND CORRUPTION</h3>
      <p>Employees are prohibited from, directly or indirectly, soliciting, accepting or obtaining or agreeing to accept or attempting to obtain, from any party for themselves or for any other party, any bribe or gratification as an inducement or a reward for doing or forbearing to do, or for having done or forborne to do, any act in relation to Aurous Consultancy's affairs or business.</p>
      
      <h3>2. RECEIVING FACILITATION PAYMENTS</h3>
      <p>Employees are prohibited from, directly or indirectly, accepting or obtaining or attempting to accept or obtain facilitation payments from any person for themselves or for any other person subject to this policy. The term "facilitation payments" generally means payments made to secure or expedite the performance by a person performing a routine or administrative duty or function.</p>
      
      <h3>3. PROHIBITION ON COMMISSIONS, DISCOUNTS AND SECRET PROFITS</h3>
      <p>Employees must not, directly or indirectly, receive or obtain, in respect of any goods or services purchased or other business transacted by or on behalf of Aurous Consultancy, any discount, rebate, commission, service, interest, consideration of value or other benefit or payments of any kind which is not authorised by Aurous Consultancy's policies or procedures.</p>
      
      <h3>4. RECEIVING GIFTS AND ENTERTAINMENT</h3>
      <p>Employees are required to comply with the Code of Ethics or any other policy and procedures relating to the receipt of gifts and entertainment.</p>
      
      <h3>5. POLITICAL CONTRIBUTIONS, SPONSORSHIPS AND DONATIONS</h3>
      <p><strong>POLITICAL CONTRIBUTIONS:</strong> Aurous Consultancy does not make or offer monetary or in-kind political contributions to political parties, political party officials or candidates for political office.</p>
      <p><strong>SPONSORSHIPS AND DONATIONS:</strong> Employees must ensure that all sponsorships and donations are not used as a subterfuge for bribery or used to circumvent or avoid any of the provisions of the COE, including in particular, the prohibition on bribery.</p>
      
      <h3>6. REPORTING OF A CONCERN AND WHISTLE-BLOWING</h3>
      <p>Recognising the abovementioned values, Aurous Consultancy provides an avenue for all employees of Aurous Consultancy, members of the public and stakeholders to disclose any improper conduct within Aurous Consultancy.</p>
      <p>All disclosures of improper conduct within Aurous Consultancy are to be channelled in accordance with the established Aurous Consultancy's Whistleblowing Policy and Procedural Manual.</p>
    `
  },
  'whistle-blowing': {
    title: 'Whistle Blowing Policy',
    icon: AlertTriangle,
    content: `
      <h2>Overview</h2>
      <p>Aurous Consultancy's Whistle-blowing Policy is aimed at achieving and maintaining high standards of conduct at work, openness and accountability.</p>
      
      <h2>Key Principles</h2>
      <ul>
        <li>Employees and stakeholders, including suppliers are encouraged to report genuine concerns about any misconduct without fear of reprisals should they act in good faith.</li>
        <li>Whistleblowers are encouraged to put their names to allegations because relevant follow-up enquiry and investigation may not be possible unless the source of information is identified.</li>
        <li>Aurous Consultancy will take all reasonable steps to protect the whistle-blower against any discrimination, retaliation or harassment.</li>
        <li>Party that retaliates against someone who has reported a wrongdoing in good faith may be subject to appropriate action, up to and including legal action, where applicable.</li>
        <li>If the subsequent investigation reveals that the disclosure was made with malicious intent, appropriate action will be taken against the whistleblower.</li>
      </ul>
      
      <h2>Definition of Misconduct</h2>
      <p>'Misconduct' means any unethical behaviour, malpractice, illegal act or failure to comply with Aurous Consultancy's policy and procedures, including but not limited to:</p>
      <ol>
        <li>Concerns about Aurous Consultancy's accounting treatments, internal controls or auditing matters</li>
        <li>Impropriety, corruption, act of fraud and theft</li>
        <li>Misuse of Aurous Consultancy's property, assets or resources</li>
        <li>Conduct which is an offence or miscarriage of law</li>
        <li>Abuse of power of authority</li>
        <li>Gross mismanagement within Aurous Consultancy</li>
        <li>Serious conflict of interest without disclosure</li>
      </ol>
      
      <h2>Reporting Channels</h2>
      <p>A whistleblower may report his/her concerns to the following designated officers:</p>
      <ul>
        <li>Office admin of Aurous @ <a href="mailto:admin@aurousconsultancy.com">admin@aurousconsultancy.com</a></li>
        <li>Audit Committee @ <a href="mailto:audit@aurousconsultancy.com">audit@aurousconsultancy.com</a></li>
      </ul>
    `
  },
  'sustainability': {
    title: 'Sustainability Report',
    icon: Leaf,
    content: `
      <p class="lead">At Aurous Consultancy, we believe that technology is a powerful tool to not only drive business growth but also create a sustainable future. Our goal is to integrate sustainability into every aspect of our work, from the solutions we deliver to the way we operate internally.</p>
      
      <h2>1. Environmental Sustainability</h2>
      
      <h3>a. Reducing Carbon Footprint Through Remote Work</h3>
      <p>As a digital-first IT consultancy, we have embraced remote work and digital tools, significantly reducing the need for business travel and office commuting. This shift has resulted in a substantial reduction in our carbon footprint.</p>
      
      <h3>b. Energy-Efficient IT Infrastructure</h3>
      <p>At Aurous Consultancy, we prioritize energy-efficient technology solutions. Our data centers and IT infrastructure are designed to minimize energy consumption. We are actively transitioning to cloud-based services with leading providers who focus on sustainability and renewable energy.</p>
      
      <h3>c. Paperless Operations</h3>
      <p>In line with our commitment to sustainability, Aurous Consultancy operates a paperless environment. We manage all documents, communications, and processes digitally, reducing paper usage and minimizing waste.</p>
      
      <h3>d. Sustainable Procurement</h3>
      <p>We are committed to sourcing sustainable products and services. Our procurement strategy focuses on selecting energy-efficient devices, recycling programs, and ethical manufacturing practices.</p>
      
      <h2>2. Social Sustainability</h2>
      
      <h3>a. Promoting Diversity, Equity, and Inclusion (DEI)</h3>
      <p>Aurous Consultancy is committed to fostering a diverse and inclusive workplace. We actively recruit from diverse talent pools, encourage gender equality, and ensure a fair, respectful, and inclusive environment for all employees.</p>
      
      <h3>b. Employee Well-being and Work-Life Balance</h3>
      <p>Our employees are at the core of our success. We prioritize their well-being by offering flexible work hours, mental health resources, and support for achieving a healthy work-life balance.</p>
      
      <h3>c. Community Engagement and Corporate Social Responsibility (CSR)</h3>
      <p>Aurous Consultancy believes in giving back to the community. We support local charities, educational programs, and initiatives that promote digital literacy, STEM education, and sustainability.</p>
      
      <h2>3. Economic Sustainability</h2>
      
      <h3>a. Sustainable Business Practices</h3>
      <p>Aurous Consultancy is committed to sustainable economic growth. We ensure that our business operations are aligned with long-term sustainability goals, focusing on responsible financial management, operational efficiency, and value creation for all stakeholders.</p>
      
      <h3>b. Delivering Sustainable IT Solutions for Clients</h3>
      <p>Our consulting services help clients adopt IT solutions that are both economically and environmentally sustainable. We advise businesses on optimizing their technology stacks to reduce energy consumption, lower operational costs, and adopt green IT practices.</p>
      
      <h2>4. Future Goals and Commitments</h2>
      <ul>
        <li><strong>Achieve Carbon Neutrality by 2030:</strong> Our long-term goal is to achieve carbon neutrality by 2030.</li>
        <li><strong>Expand Sustainable IT Services:</strong> We aim to expand our portfolio of sustainable IT services.</li>
        <li><strong>Promote Digital Inclusion:</strong> We are committed to reducing the digital divide by providing technology solutions and training to underserved communities.</li>
      </ul>
      
      <p>If you would like to learn more about our sustainability initiatives, please reach out to us at <a href="mailto:enquiry@aurousconsultancy.com">enquiry@aurousconsultancy.com</a>.</p>
    `
  },
  'anti-slavery': {
    title: 'Modern Slavery, Forced Labour, and Human Trafficking Policy',
    icon: Users,
    content: `
      <p class="lead">At Aurous Consultancy, we are committed to upholding the highest standards of social compliance to protect the rights of workers across all operations and in our clients' supply chains. We proactively identify and manage potential risks to ensure ethical practices are upheld at every level.</p>
      
      <h2>Prohibiting Forced Labour</h2>
      <p>At Aurous Consultancy we adopt a zero-tolerance approach to forced labour. We ensure transparent employment practices for all our employees and emphasize the importance of ethical labour standards with our clients and partners.</p>
      <p>We advocate for the non-retention of personal documents, allowing workers to freely hold their passports and identity documents. Additionally, we require all our clients, suppliers, and subcontractors to acknowledge and comply with a Code of Conduct prohibiting forced labour.</p>
      <p>To date, we have maintained a record of <strong>zero incidents</strong> of forced labour reported within our direct operations or through our consultancy projects.</p>
      
      <h2>Prohibiting Child Labour</h2>
      <p>In strict adherence to our Child Labour Protection Policy, Aurous Consultancy does not condone the employment of child labour (under 15 years of age) or young workers in hazardous roles (under 18 years of age).</p>
      <p>We ensure that all our clients, suppliers, and subcontractors comply with this policy through regular audits and mandatory adherence to the Code of Conduct.</p>
      <p>In instances of non-compliance, we advocate for corrective actions, including supplier education and training. Persistent non-compliance or repeated violations result in the termination of business relationships.</p>
      <p>Through these stringent measures, Aurous has successfully ensured <strong>zero reported cases</strong> of child labour or violations involving young workers under the ages of 15 and 18.</p>
    `
  },
  'pdpa': {
    title: 'Personal Data Protection Notice',
    icon: Lock,
    content: `
      <h2>INTRODUCTION</h2>
      <ul>
        <li>This Personal Data Protection Notice ("Notice") describes how Aurous Consultancy uses your personal data.</li>
        <li>Aurous Consultancy is fully committed to ensure that our processing of personal data complies with the Personal Data Protection Act 2010 (PDPA).</li>
        <li>This Notice relates to your personal data that you have voluntarily provided to Aurous Consultancy when you access our services.</li>
      </ul>
      
      <h2>PURPOSE OF NOTICE</h2>
      <p>This notice is issued pursuant to the PDPA to all individuals who are customers, smart partners, stakeholders, vendors, and/or providers of goods and/or services and their employees.</p>
      
      <h2>PURPOSE OF PROCESSING PERSONAL DATA</h2>
      <p>Aurous Consultancy processes personal data for the following purposes:</p>
      <ul>
        <li>To provide financial education and financial counselling services</li>
        <li>To develop a personalised debt management programme (DMP)</li>
        <li>To evaluate the effectiveness of our programmes</li>
        <li>To assess vendor suitability for awarding any contract applications</li>
        <li>To conduct customer satisfaction surveys</li>
        <li>To provide information on our product and services</li>
        <li>To verify information relating to personal particulars, employment background, and financial standing</li>
        <li>Other legitimate business activities of Aurous Consultancy</li>
      </ul>
      
      <h2>TYPES OF PERSONAL DATA PROCESSED</h2>
      <ul>
        <li><strong>Personal particulars:</strong> name, race, religion, date of birth, age, gender, address, contact details, NRIC number, passport number, marital status, details of spouse, dependents and next of kin and education background</li>
        <li>Employment history</li>
        <li>Loan and bank account number & balances and statutory contributions</li>
        <li>Information on asset, liability and credit worthiness</li>
      </ul>
      
      <h2>DISCLOSURE OF PERSONAL DATA</h2>
      <p>Aurous Consultancy may disclose your personal data to the following parties:</p>
      <ul>
        <li>Governmental authorities to comply with statutory, regulatory and governmental requirements</li>
        <li>Any other relevant entities, including banks, insurer, takaful operators, development financial institutions and cooperatives</li>
      </ul>
      
      <h2>ACCESS OF PERSONAL DATA</h2>
      <p>Under the PDPA, you have the right to access your personal data to ensure that the personal data we hold about you is accurate, complete, not misleading and up-to-date.</p>
      
      <h2>DATA SECURITY</h2>
      <p>Aurous Consultancy takes practical steps to protect the collected personal data and its confidentiality. Aurous Consultancy ensures that appropriate administrative, security safeguards and procedures taken are in line with the applicable laws and regulations for the protection of personal data.</p>
      
      <h2>DATA RETENTION</h2>
      <p>Aurous Consultancy ensures that the collected personal data will be retained and destroyed as prescribed in section 10 of the PDPA.</p>
      
      <h2>CONTACT</h2>
      <p>Aurous Consultancy<br/>4800-3-2, CBD Perdana 1<br/>Jalan Perdana, Cyberjaya<br/>Selangor, Malaysia - 63000</p>
      <p>Phone: <a href="tel:+60383220562">+60 3-8322 0562</a><br/>Email: <a href="mailto:enquiry@aurousconsultancy.com">enquiry@aurousconsultancy.com</a></p>
    `
  }
};

export default function PolicyPage() {
  const { policyId } = useParams();
  const policy = policies[policyId];
  
  if (!policy) {
    return (
      <WebsiteLayout>
        <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white">Policy Not Found</h1>
            <Link to="/">
              <Button className="mt-8">Go Home</Button>
            </Link>
          </div>
        </section>
      </WebsiteLayout>
    );
  }
  
  const Icon = policy.icon;
  
  return (
    <WebsiteLayout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-blue-600/20 flex items-center justify-center">
              <Icon className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">{policy.title}</h1>
          </div>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-16 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-slate-900 dark:prose-headings:text-white prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-li:text-slate-600 dark:prose-li:text-slate-300 prose-strong:text-slate-900 dark:prose-strong:text-white"
            dangerouslySetInnerHTML={{ __html: policy.content }}
          />
        </div>
      </section>
    </WebsiteLayout>
  );
}